import { inject, Service } from '@angular/core'
import { map, Observable } from 'rxjs'
import { Control } from 'superlikers-dynamic-forms'
import {
  LoginResponse,
  LogoutResponse,
  NotificationChannel,
  ParticipantService,
  PasswordService,
  RegistrationsService,
  SendResetTokenResponse,
  SessionService,
  UpdateParticipantResponse,
  ValidateChannelResponse
} from '@superlikers/microsites-api'

import { formFieldsAdapter, userAdapter } from '@core/data/adapters'
import { Participant, User } from '@core/data/models'

@Service()
export class AuthApi {
  #sessionService = inject(SessionService)
  #participantService = inject(ParticipantService)
  #passwordService = inject(PasswordService)
  #registrationsService = inject(RegistrationsService)

  /** Usuario en sesión, o `null` si no hay ninguna activa. */
  fetchCurrentUser(): Observable<User | null> {
    return this.#participantService.getCurrent<Participant>().pipe(map(response => (response.participation.authenticated ? userAdapter(response.participation) : null)))
  }

  getLoginControls(): Observable<Control[]> {
    return this.#sessionService.getLoginForm().pipe(map(response => formFieldsAdapter(response.fields)))
  }

  getSignupControls(): Observable<Control[]> {
    return this.#registrationsService.getSignupForm().pipe(map(response => formFieldsAdapter(response.fields)))
  }

  updateParticipant(data: Record<string, unknown>): Observable<UpdateParticipantResponse> {
    return this.#registrationsService.updateParticipant(data)
  }

  login(data: Record<string, string>): Observable<LoginResponse> {
    return this.#sessionService.login(data)
  }

  logout(): Observable<LogoutResponse> {
    return this.#sessionService.logout()
  }

  checkRequirements() {
    return this.#sessionService.checkLoginRequirements()
  }

  validateChannel(channel: NotificationChannel, value: string): Observable<ValidateChannelResponse> {
    return this.#passwordService.validateChannel(channel, value)
  }

  sendToken(channel: NotificationChannel, data: Record<string, string>, redirectUrl: string): Observable<SendResetTokenResponse> {
    return this.#passwordService.sendResetToken({ channel, data, edit_password_url: redirectUrl })
  }

  updatePassword(password: string, passwordConfirmation: string, token: string, channel: NotificationChannel = 'email') {
    return this.#passwordService.resetPassword({ password, password_confirmation: passwordConfirmation, token, channel })
  }
}
